import { z } from 'zod';

import { locationSchema, moneySchema } from '@/schemas/commonSchemas';
import { deliveryMapSchema, deliverySchema } from '@/schemas/deliverySchemas';
import { vehicleSchema } from '@/schemas/vehicleSchemas';

// ============================================
// Common Type Schemas
// ============================================

const typeBaseSchema = z.object({
  uuid: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});

const typeMapSchema = z.record(z.string(), typeBaseSchema);

// ============================================
// User & Authentication Schemas
// ============================================

const phoneNumberSchema = z.object({
  uuid: z.string().uuid(),
  phoneNumber: z.string(),
  phoneNumberType: z.string().optional(),
  isPrimary: z.boolean().optional(),
});

const addressSchema = z.object({
  uuid: z.string().uuid(),
  line1: z.string(),
  line2: z.string().optional(),
  line3: z.string().optional(),
  city: z.string(),
  county: z.string().optional(),
  postcode: z.string(),
  countryCode: z.string().length(2),
  location: locationSchema.optional(),
});

const companySchema = z.object({
  uuid: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().optional(),
  logoUrl: z.string().url().optional(),
  companyTypeSlug: z.string().optional(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const userSchema = z.object({
  uuid: z.string().uuid(),
  firstname: z.string(),
  lastname: z.string(),
  email: z.string().email(),
  emailVerifiedAt: z.string().datetime().nullable(),
  imgUrl: z.string().url().nullable(),
  imgThumbUrl: z.string().url().nullable(),
  phoneNumbers: z.array(phoneNumberSchema).optional(),
  addresses: z.array(addressSchema).optional(),
  companies: z.array(companySchema).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const loginResponseSchema = z.object({
  user: userSchema,
  token: z.string().optional(),
  message: z.string().optional(),
});

const meResponseSchema = z.object({
  data: userSchema,
});

const roleSchema = z.object({
  uuid: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  permissions: z.array(z.string()).optional(),
});

// ============================================
// Driver Schemas
// ============================================

const driverInfoSchema = z.object({
  uuid: z.string().uuid(),
  driverUserUuid: z.string().uuid(),
  driverStatusSlug: z.string(),
  driverStatusName: z.string(),
  licenceNumber: z.string().optional(),
  insuranceNumber: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const driverMapSchema = z.record(z.string().uuid(), driverInfoSchema);

// ============================================
// Parcel Schemas
// ============================================

const parcelSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  description: z.string().optional(),
  volumeCm3: z.number(),
  weightKg: z.number(),
  quantity: z.number().int(),
  value: moneySchema.optional(),
  isFragile: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const parcelMapSchema = z.record(z.string().uuid(), parcelSchema);

// ============================================
// Delivery Additional Schemas
// ============================================

const deliveryStatusSchema = z.object({
  uuid: z.string().uuid(),
  deliveryUuid: z.string().uuid(),
  statusType: z.string(),
  statusSlug: z.string(),
  statusMessage: z.string().optional(),
  createdAt: z.string().datetime(),
  createdByUserUuid: z.string().uuid().optional(),
});

const deliveryQuoteSchema = z.object({
  uuid: z.string().uuid(),
  deliveryUuid: z.string().uuid(),
  price: moneySchema,
  validUntil: z.string().datetime(),
  isAccepted: z.boolean(),
  createdAt: z.string().datetime(),
});

const deliveryContactSchema = z.object({
  uuid: z.string().uuid(),
  firstname: z.string(),
  lastname: z.string(),
  email: z.string().email().optional(),
  phoneNumber: z.string().optional(),
  companyName: z.string().optional(),
});

const deliveryLocationHistorySchema = z.object({
  uuid: z.string().uuid(),
  deliveryUuid: z.string().uuid(),
  location: locationSchema,
  recordedAt: z.string().datetime(),
  speed: z.number().optional(),
  heading: z.number().optional(),
  accuracy: z.number().optional(),
});

const deliveryVehicleDriverSchema = z.object({
  deliveryUuid: z.string().uuid(),
  vehicleUuid: z.string().uuid(),
  driverUserUuids: z.array(z.string().uuid()),
  assignedAt: z.string().datetime(),
  unassignedAt: z.string().datetime().nullable(),
});

// ============================================
// Note Schemas
// ============================================

interface NoteType {
  uuid: string;
  noteableType: string;
  noteableUuid: string;
  noteCategorySlug: string;
  noteTypeSlug: string;
  message: string;
  createdByUserUuid: string;
  createdAt: string;
  updatedAt: string;
  replies?: NoteType[];
}

const noteSchema: z.ZodType<NoteType> = z.object({
  uuid: z.string().uuid(),
  noteableType: z.string(),
  noteableUuid: z.string().uuid(),
  noteCategorySlug: z.string(),
  noteTypeSlug: z.string(),
  message: z.string(),
  createdByUserUuid: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  replies: z.array(z.lazy((): z.ZodType<NoteType> => noteSchema)).optional(),
});

// Legacy API Note Schema (snake_case)
interface LegacyApiNoteType {
  uuid: string;
  noteable_type_slug: string;
  noteable_uuid: string;
  note_type_slug: string;
  note_data: {
    content: string;
    subject?: string;
  };
  parent_note_uuid: string | null;
  user_uuid: string;
  user_name?: string;
  user_avatar_url?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  replies?: LegacyApiNoteType[];
}

const legacyApiNoteSchema: z.ZodType<LegacyApiNoteType> = z.object({
  uuid: z.string().uuid(),
  noteable_type_slug: z.string(),
  noteable_uuid: z.string().uuid(),
  note_type_slug: z.string(),
  note_data: z.object({
    content: z.string(),
    subject: z.string().optional(),
  }),
  parent_note_uuid: z.string().nullable(),
  user_uuid: z.string().uuid(),
  user_name: z.string().optional(),
  user_avatar_url: z.string().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  deleted_at: z.string().datetime().nullable().optional(),
  replies: z.array(z.lazy((): z.ZodType<LegacyApiNoteType> => legacyApiNoteSchema)).optional(),
});

// New API Note Resource Schema
interface NoteResourceType {
  uuid: string;
  id?: number;
  noteTypeSlug: string;
  noteableType: string;
  noteableUuid: string;
  parentNoteUuid?: string;
  noteData: {
    subject?: string;
    content: string;
    metadata?: Record<string, unknown>;
  };
  creatorContext: {
    userUuid: string;
    userName: string;
    userType: string;
    companyUuid?: string;
    companyName?: string;
    ipAddress?: string;
    userAgent?: string;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  replies?: NoteResourceType[];
  replyCount?: number;
}

const noteResourceSchema: z.ZodType<NoteResourceType> = z.object({
  uuid: z.string().uuid(),
  id: z.number().optional(),
  noteTypeSlug: z.string(),
  noteableType: z.string(),
  noteableUuid: z.string().uuid(),
  parentNoteUuid: z.string().uuid().optional(),
  noteData: z.object({
    subject: z.string().optional(),
    content: z.string(),
    metadata: z.record(z.unknown()).optional(),
  }),
  creatorContext: z.object({
    userUuid: z.string().uuid(),
    userName: z.string(),
    userType: z.string(),
    companyUuid: z.string().uuid().optional(),
    companyName: z.string().optional(),
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
  }),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().optional(),
  replies: z.array(z.lazy((): z.ZodType<NoteResourceType> => noteResourceSchema)).optional(),
  replyCount: z.number().optional(),
});

// ============================================
// Calendar Event Schema
// ============================================

const calendarEventSchema = z.object({
  uuid: z.string().uuid(),
  title: z.string(),
  description: z.string().optional(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  allDay: z.boolean(),
  eventType: z.string(),
  eventUuid: z.string().uuid().optional(),
  color: z.string().optional(),
});

// ============================================
// API Response Schemas
// ============================================

// Generic paginated response
const paginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    data: z.array(itemSchema),
    meta: z.object({
      current_page: z.number(),
      from: z.number().nullable(),
      last_page: z.number(),
      per_page: z.number(),
      to: z.number().nullable(),
      total: z.number(),
    }),
    links: z.object({
      first: z.string().nullable(),
      last: z.string().nullable(),
      prev: z.string().nullable(),
      next: z.string().nullable(),
    }),
  });

// Generic data wrapper
const dataResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: dataSchema,
  });

// Generic map response
const mapResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    data: z.record(z.string(), itemSchema),
  });

// ============================================
// Delivery Document Schemas
// ============================================

const documentSchema = z.object({
  uuid: z.string().uuid(),
  createdAt: z.string().datetime(),
  isChecked: z.boolean(),
  isValid: z.boolean(),
  documentTypeSlug: z.string(),
  sizeBytes: z.number(),
  originalName: z.string(),
  mimeType: z.string(),
  extension: z.string(),
});

const deliveryStepTaskDocInfoSchema = z.object({
  proofName: z.string(),
  deliveryStepTaskProofTypeSlug: z.string(),
  url: z.string(),
  document: documentSchema,
});

const deliveryStepTaskDocSchema = z.object({
  deliveryUuid: z.string().uuid(),
  deliverySteps: z.record(
    z.string().uuid(),
    z.object({
      deliveryStepUuid: z.string().uuid(),
      deliveryStepOrder: z.number(),
      deliveryStepTasks: z.record(
        z.string().uuid(),
        z.object({
          deliveryStepTaskUuid: z.string().uuid(),
          deliveryStepTaskTypeSlug: z.string(),
          deliveryStepTaskOrder: z.number(),
          documents: z.record(z.string().uuid(), deliveryStepTaskDocInfoSchema),
        }),
      ),
    }),
  ),
});

// ============================================
// Configuration Management Schemas
// ============================================

const configurationValueTypeSchema = z.enum(['string', 'integer', 'boolean', 'float', 'json', 'array']);
const configurationSourceSchema = z.enum(['company', 'system', 'default']);
const configurationChangeTypeSchema = z.enum(['created', 'updated', 'deleted', 'reset']);

const configurationDefinitionSchema = z.object({
  uuid: z.string().uuid(),
  key: z.string(),
  category: z.string(),
  name: z.string(),
  description: z.string().optional(),
  defaultValue: z.string(),
  valueType: configurationValueTypeSchema,
  isEditableRuntime: z.boolean(),
  validationRules: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const systemConfigurationSchema = z.object({
  uuid: z.string().uuid(),
  key: z.string(),
  value: z.string(),
  valueType: configurationValueTypeSchema,
  description: z.string().optional(),
  isEditableRuntime: z.boolean(),
  definition: configurationDefinitionSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const companyConfigurationSchema = z.object({
  uuid: z.string().uuid(),
  companyUuid: z.string().uuid(),
  key: z.string(),
  value: z.string(),
  valueType: configurationValueTypeSchema,
  source: configurationSourceSchema,
  description: z.string().optional(),
  isEditableRuntime: z.boolean(),
  definition: configurationDefinitionSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const companyConfigurationHistorySchema = z.object({
  uuid: z.string().uuid(),
  companyConfigurationUuid: z.string().uuid(),
  changeType: configurationChangeTypeSchema,
  oldValue: z.string().optional(),
  newValue: z.string().optional(),
  userUuid: z.string().uuid(),
  userName: z.string().optional(),
  notes: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// ============================================
// Complete API Schema Mappings
// ============================================

export const apiSchemas: Record<string, z.ZodSchema> = {
  // Authentication & User Management
  'POST /api/v1/login': loginResponseSchema,
  'POST /api/v1/logout': z.object({ message: z.string() }),
  'GET /api/v1/me': meResponseSchema,
  'GET /api/v1/me/role': dataResponseSchema(z.array(roleSchema)),
  'GET /api/v1/me/address': dataResponseSchema(z.array(addressSchema)),
  'GET /api/v1/me/company': dataResponseSchema(z.array(companySchema)),
  'POST /api/v1/me/name': meResponseSchema,
  'POST /api/v1/me/email': meResponseSchema,
  'POST /api/v1/me/phonenumber': meResponseSchema,
  'POST /api/v1/me/password': z.object({ message: z.string() }),
  'POST /api/v1/me/country/default': meResponseSchema,
  'POST /api/v1/me/profile/picture': z.object({
    profilePictureUrl: z.string().url(),
    profilePictureThumbUrl: z.string().url(),
    message: z.string(),
  }),
  'POST /api/v1/me/document': dataResponseSchema(documentSchema),

  // Company User Management
  'GET /api/v1/c/:companySlug/admin/user': mapResponseSchema(userSchema),
  'GET /api/v1/c/:companySlug/admin/user/admin/uuid': dataResponseSchema(z.array(z.string().uuid())),
  'POST /api/v1/c/:companySlug/admin/user/:userId/status': dataResponseSchema(userSchema),

  // Vehicle Management
  'GET /api/v1/type/vehicle': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/vehicle/status': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/vehicle/size': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/vehicle/colour': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/vehicle/feature': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/vehicle/make': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/vehicle/model': mapResponseSchema(
    z.object({
      uuid: z.string().uuid(),
      vehicleMakeTypeSlug: z.string(),
      name: z.string(),
      vehicleSizeTypeSlug: z.string(),
    }),
  ),
  'GET /api/v1/c/:companySlug/admin/vehicle': mapResponseSchema(vehicleSchema),
  'POST /api/v1/c/:companySlug/admin/vehicle': dataResponseSchema(vehicleSchema),
  'DELETE /api/v1/c/:companySlug/admin/vehicle': z.object({ message: z.string() }),
  'PUT /api/v1/c/:companySlug/admin/vehicle/:vehicleUuid': dataResponseSchema(vehicleSchema),
  'POST /api/v1/c/:companySlug/admin/vehicle/status': dataResponseSchema(vehicleSchema),
  'GET /api/v1/c/:companySlug/admin/fleet': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        name: z.string(),
        description: z.string().optional(),
        vehicleUuids: z.array(z.string().uuid()),
      }),
    ),
  ),

  // Driver Management
  'GET /api/v1/type/driver/status': dataResponseSchema(typeMapSchema),
  'GET /api/v1/c/:companySlug/admin/driver/user/uuid': dataResponseSchema(z.array(z.string().uuid())),
  'GET /api/v1/c/:companySlug/admin/driver/info': dataResponseSchema(driverMapSchema),
  'POST /api/v1/c/:companySlug/admin/driver': dataResponseSchema(driverInfoSchema),
  'PUT /api/v1/c/:companySlug/admin/driver/:driverUserUuid': dataResponseSchema(driverInfoSchema),
  'DELETE /api/v1/c/:companySlug/admin/driver/:driverUserUuid': z.object({ message: z.string() }),
  'POST /api/v1/c/:companySlug/admin/driver/status': dataResponseSchema(driverInfoSchema),

  // Delivery Management
  'GET /api/v1/type/delivery/cancel': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/delivery/live/status': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/delivery/schedule/window': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/delivery/quote/status': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/delivery/step/task': dataResponseSchema(typeMapSchema),
  'GET /api/v1/c/:companySlug/admin/delivery': dataResponseSchema(deliveryMapSchema),
  'GET /api/v1/c/:companySlug/admin/delivery/step': mapResponseSchema(
    z.object({
      uuid: z.string().uuid(),
      deliveryUuid: z.string().uuid(),
      stepOrderNo: z.number().int(),
      addressUuid: z.string().uuid().optional(),
      location: locationSchema.optional(),
    }),
  ),
  'GET /api/v1/c/:companySlug/admin/delivery/step/task': mapResponseSchema(
    z.object({
      uuid: z.string().uuid(),
      deliveryStepTaskUuid: z.string().uuid(),
      deliveryStepUuid: z.string().uuid(),
      deliveryStepTaskTypeSlug: z.string(),
      taskOrderNo: z.number().int(),
      completedAt: z.string().datetime().nullable(),
    }),
  ),
  'GET /api/v1/c/:companySlug/admin/delivery/parcel': dataResponseSchema(parcelMapSchema),
  'GET /api/v1/c/:companySlug/admin/delivery/quote': mapResponseSchema(deliveryQuoteSchema),
  'GET /api/v1/c/:companySlug/admin/delivery/status': mapResponseSchema(deliveryStatusSchema),
  'GET /api/v1/c/:companySlug/admin/delivery/address': mapResponseSchema(addressSchema),
  'GET /api/v1/c/:companySlug/admin/delivery/contact': mapResponseSchema(deliveryContactSchema),
  'GET /api/v1/c/:companySlug/admin/delivery/location': mapResponseSchema(z.array(deliveryLocationHistorySchema)),
  'GET /api/v1/c/:companySlug/admin/delivery/vehicle/driver': mapResponseSchema(z.array(deliveryVehicleDriverSchema)),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/vehicle': dataResponseSchema(
    z.object({ message: z.string() }),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/vehicle/driver': dataResponseSchema(
    z.object({ message: z.string() }),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/status': dataResponseSchema(deliveryStatusSchema),
  'POST /api/v1/c/:companySlug/admin/delivery/create-delivery': dataResponseSchema(deliverySchema),

  // Additional Delivery Endpoints
  'GET /api/v1/c/:companySlug/admin/delivery/step/task/doc': dataResponseSchema(
    z.record(z.string().uuid(), deliveryStepTaskDocSchema),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/doc/step/task/proof': dataResponseSchema(
    z.object({
      message: z.string(),
      document: documentSchema.optional(),
    }),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/add-contact-to-step-task': dataResponseSchema(
    z.object({
      message: z.string(),
      contact: deliveryContactSchema.optional(),
    }),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/add-delivery-step-with-address': dataResponseSchema(
    z.object({
      deliveryStep: z.object({
        uuid: z.string().uuid(),
        deliveryUuid: z.string().uuid(),
        stepOrderNo: z.number().int(),
        addressUuid: z.string().uuid(),
        location: locationSchema.optional(),
      }),
      address: addressSchema,
    }),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/parcel/:parcelUuid/add-contact': dataResponseSchema(
    z.object({
      message: z.string(),
      contact: deliveryContactSchema.optional(),
    }),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/add-delivery-step-pickup': dataResponseSchema(
    z.object({
      message: z.string(),
      deliveryStepTask: z
        .object({
          uuid: z.string().uuid(),
          deliveryStepUuid: z.string().uuid(),
          deliveryStepTaskTypeSlug: z.string(),
          taskOrderNo: z.number().int(),
          completedAt: z.string().datetime().nullable(),
        })
        .optional(),
    }),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/add-delivery-step-dropoff': dataResponseSchema(
    z.object({
      message: z.string(),
      deliveryStepTask: z
        .object({
          uuid: z.string().uuid(),
          deliveryStepUuid: z.string().uuid(),
          deliveryStepTaskTypeSlug: z.string(),
          taskOrderNo: z.number().int(),
          completedAt: z.string().datetime().nullable(),
        })
        .optional(),
    }),
  ),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/add-parcel-to-step-task': dataResponseSchema(
    z.object({
      message: z.string(),
      parcel: parcelSchema.optional(),
    }),
  ),

  // Parcel Management
  'GET /api/v1/c/:companySlug/admin/parcel/uuid': dataResponseSchema(z.array(z.string().uuid())),
  'GET /api/v1/c/:companySlug/admin/parcel/info': dataResponseSchema(parcelMapSchema),
  'POST /api/v1/c/:companySlug/admin/parcel': dataResponseSchema(parcelSchema),
  'PUT /api/v1/c/:companySlug/admin/parcel/:parcelUuid': dataResponseSchema(parcelSchema),
  'DELETE /api/v1/c/:companySlug/admin/parcel/:parcelUuid': z.object({ message: z.string() }),

  // Document Management
  'GET /api/v1/type/document': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/document/format': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/document/section': dataResponseSchema(typeMapSchema),

  // Originator Management
  'GET /api/v1/type/o': dataResponseSchema(typeMapSchema),
  'GET /api/v1/c/:companySlug/admin/o/account': mapResponseSchema(
    z.object({
      uuid: z.string().uuid(),
      originatorSlug: z.string(),
      name: z.string(),
      externalId: z.string().optional(),
      settings: z.record(z.unknown()).optional(),
    }),
  ),

  // Orkestro Integration
  'GET /api/v1/type/orkestro/action': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/orkestro/delivery/live/status': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/orkestro/delivery/live/status/payload': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/orkestro/request/status': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/orkestro/schedule': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/orkestro/service': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/orkestro/transport': dataResponseSchema(typeMapSchema),

  // Notes Management
  'GET /api/v1/type/note/noteable': dataResponseSchema(typeMapSchema),
  'GET /api/v1/type/note/types': dataResponseSchema(typeMapSchema),
  'GET /api/v1/c/:companySlug/admin/notes/:noteableTypePlural/:noteableUuid':
    paginatedResponseSchema(legacyApiNoteSchema),
  'GET /api/v1/c/:companySlug/admin/notes/:note_uuid': dataResponseSchema(noteResourceSchema),
  'POST /api/v1/c/:companySlug/admin/notes': dataResponseSchema(noteResourceSchema),
  'PUT /api/v1/c/:companySlug/admin/notes/:note_uuid': dataResponseSchema(noteResourceSchema),
  'DELETE /api/v1/c/:companySlug/admin/notes/:note_uuid': z.object({ message: z.string() }),
  // Additional note endpoints with different parameter names
  'GET /api/v1/c/:companySlug/admin/notes/:note': dataResponseSchema(noteResourceSchema),
  'PUT /api/v1/c/:companySlug/admin/notes/:note': dataResponseSchema(noteResourceSchema),
  'DELETE /api/v1/c/:companySlug/admin/notes/:note': z.object({ message: z.string() }),
  // Legacy note endpoints (without /api/v1 prefix)
  'GET /type/note/noteable': dataResponseSchema(typeMapSchema),
  'GET /type/note/types': dataResponseSchema(typeMapSchema),
  'POST /c/:companySlug/admin/notes': dataResponseSchema(legacyApiNoteSchema),
  'GET /c/:companySlug/admin/notes/:note_uuid': dataResponseSchema(legacyApiNoteSchema),
  'PUT /c/:companySlug/admin/notes/:note_uuid': dataResponseSchema(legacyApiNoteSchema),
  'DELETE /c/:companySlug/admin/notes/:note_uuid': z.object({ message: z.string() }),

  // Email & Beta
  'POST /api/v1/email/join': z.object({
    message: z.string(),
    success: z.boolean().optional(),
  }),

  // Calendar Events (if used)
  'GET /api/v1/c/:companySlug/admin/calendar/events': dataResponseSchema(z.array(calendarEventSchema)),

  // ============================================
  // Authentication Token Management
  // ============================================
  'POST /api/v1/auth/refresh': z.object({
    access_token: z.string(),
    refresh_token: z.string(),
    token_type: z.literal('Bearer'),
    expires_in: z.number(),
    scope: z.string().optional(),
    issued_at: z.string().datetime(),
  }),
  'POST /api/v1/auth/revoke': z.object({
    message: z.string(),
  }),
  'POST /api/v1/auth/validate': z.object({
    valid: z.boolean(),
    token: z
      .object({
        id: z.string(),
        name: z.string(),
        abilities: z.array(z.string()),
        lastUsedAt: z.string().datetime().nullable(),
        expiresAt: z.string().datetime().nullable(),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
        isActive: z.boolean(),
        tokenableType: z.string(),
        tokenableId: z.string(),
      })
      .optional(),
    message: z.string().optional(),
  }),
  'GET /api/v1/auth/token-info': z.object({
    user_id: z.string(),
    client_id: z.string(),
    scope: z.string().optional(),
    expires_at: z.string().datetime(),
    issued_at: z.string().datetime(),
    token_type: z.literal('Bearer'),
    active: z.boolean(),
  }),
  'GET /api/v1/auth/settings': z.object({
    token_lifetime: z.number(),
    refresh_token_lifetime: z.number(),
    auto_refresh_enabled: z.boolean(),
    session_timeout: z.number(),
    require_password_change: z.boolean(),
    last_updated: z.string().datetime(),
    updated_by: z.string(),
  }),
  'PUT /api/v1/auth/settings': z.object({
    token_lifetime: z.number(),
    refresh_token_lifetime: z.number(),
    auto_refresh_enabled: z.boolean(),
    session_timeout: z.number(),
    require_password_change: z.boolean(),
    last_updated: z.string().datetime(),
    updated_by: z.string(),
  }),

  // ============================================
  // Auth Token Resource Management
  // ============================================
  'GET /api/v1/auth-tokens': dataResponseSchema(
    z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        abilities: z.array(z.string()),
        lastUsedAt: z.string().datetime().nullable(),
        expiresAt: z.string().datetime().nullable(),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
        isActive: z.boolean(),
        tokenableType: z.string(),
        tokenableId: z.string(),
      }),
    ),
  ),
  'POST /api/v1/auth-tokens': z.object({
    token: z.object({
      id: z.string(),
      name: z.string(),
      abilities: z.array(z.string()),
      lastUsedAt: z.string().datetime().nullable(),
      expiresAt: z.string().datetime().nullable(),
      createdAt: z.string().datetime(),
      updatedAt: z.string().datetime(),
      isActive: z.boolean(),
      tokenableType: z.string(),
      tokenableId: z.string(),
    }),
    plainTextToken: z.string(),
  }),
  'GET /api/v1/auth-tokens/:tokenId': z.object({
    id: z.string(),
    name: z.string(),
    abilities: z.array(z.string()),
    lastUsedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    isActive: z.boolean(),
    tokenableType: z.string(),
    tokenableId: z.string(),
  }),
  'DELETE /api/v1/auth-tokens/:tokenId': z.object({
    message: z.string(),
  }),
  'GET /api/v1/auth-tokens/statistics': z.object({
    totalTokens: z.number(),
    activeTokens: z.number(),
    expiredTokens: z.number(),
    lastUsed: z.string().datetime().optional(),
    mostUsedToken: z
      .object({
        name: z.string(),
        usageCount: z.number(),
      })
      .optional(),
  }),
  'POST /api/v1/auth-tokens/validate': z.object({
    valid: z.boolean(),
    token: z
      .object({
        id: z.string(),
        name: z.string(),
        abilities: z.array(z.string()),
        lastUsedAt: z.string().datetime().nullable(),
        expiresAt: z.string().datetime().nullable(),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
        isActive: z.boolean(),
        tokenableType: z.string(),
        tokenableId: z.string(),
      })
      .optional(),
    message: z.string().optional(),
  }),

  // ============================================
  // 2FA Authentication
  // ============================================
  'GET /api/v1/2fa': z.object({
    enabled: z.boolean(),
    qrCode: z.string().optional(),
    secret: z.string().optional(),
    recoveryCodes: z.array(z.string()).optional(),
  }),
  'POST /api/v1/2fa': z.object({
    qr_code_url: z.string(),
    recovery_codes: z.array(z.string()),
  }),
  'DELETE /api/v1/2fa': z.object({
    message: z.string(),
  }),
  'POST /api/v1/2fa/confirm': z.object({
    message: z.string(),
  }),
  'POST /api/v1/2fa/challenge': z.object({
    message: z.string(),
  }),
  'GET /api/v1/2fa/qr-code': z.object({
    qrCode: z.string(),
    secret: z.string(),
  }),
  'POST /api/v1/2fa/recovery-codes': z.object({
    recoveryCodes: z.array(z.string()),
  }),

  // ============================================
  // Super Admin Management
  // ============================================
  'GET /api/v1/admin/system/settings': z.object({
    maintenanceMode: z.boolean(),
    registrationEnabled: z.boolean(),
    maxUsersPerCompany: z.number().optional(),
    featureFlags: z.record(z.boolean()).optional(),
    systemVersion: z.string().optional(),
  }),
  'PUT /api/v1/admin/system/settings': z.object({
    maintenanceMode: z.boolean(),
    registrationEnabled: z.boolean(),
    maxUsersPerCompany: z.number().optional(),
    featureFlags: z.record(z.boolean()).optional(),
    systemVersion: z.string().optional(),
  }),
  'POST /api/v1/admin/user-company': z.object({
    user: userSchema,
    company: companySchema,
    message: z.string(),
  }),
  'GET /api/v1/admin/audit-logs': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        userUuid: z.string().uuid(),
        companyUuid: z.string().uuid().optional(),
        action: z.string(),
        resource: z.string(),
        resourceUuid: z.string().uuid().optional(),
        oldValues: z.record(z.unknown()).optional(),
        newValues: z.record(z.unknown()).optional(),
        ipAddress: z.string().optional(),
        userAgent: z.string().optional(),
        createdAt: z.string().datetime(),
      }),
    ),
  ),
  'GET /api/v1/admin/audit-logs/user/:userUuid': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        userUuid: z.string().uuid(),
        companyUuid: z.string().uuid().optional(),
        action: z.string(),
        resource: z.string(),
        resourceUuid: z.string().uuid().optional(),
        oldValues: z.record(z.unknown()).optional(),
        newValues: z.record(z.unknown()).optional(),
        ipAddress: z.string().optional(),
        userAgent: z.string().optional(),
        createdAt: z.string().datetime(),
      }),
    ),
  ),
  'GET /api/v1/admin/audit-logs/company/:companyUuid': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        userUuid: z.string().uuid(),
        companyUuid: z.string().uuid().optional(),
        action: z.string(),
        resource: z.string(),
        resourceUuid: z.string().uuid().optional(),
        oldValues: z.record(z.unknown()).optional(),
        newValues: z.record(z.unknown()).optional(),
        ipAddress: z.string().optional(),
        userAgent: z.string().optional(),
        createdAt: z.string().datetime(),
      }),
    ),
  ),
  'POST /api/v1/admin/system/maintenance/enable': z.object({
    message: z.string(),
  }),
  'POST /api/v1/admin/system/maintenance/disable': z.object({
    message: z.string(),
  }),
  'POST /api/v1/admin/system/cache/clear': z.object({
    message: z.string(),
  }),

  // ============================================
  // Super Admin Impersonation
  // ============================================
  'GET /api/v1/sa/impersonation/status': z.object({
    isImpersonating: z.boolean(),
    originalUser: userSchema.optional(),
    impersonatedUser: userSchema.optional(),
    startedAt: z.string().datetime().optional(),
  }),
  'GET /api/v1/sa/impersonation/users': dataResponseSchema(z.array(userSchema)),
  'GET /api/v1/sa/impersonation/history': dataResponseSchema(
    z.array(
      z.object({
        id: z.string(),
        originalUserUuid: z.string().uuid(),
        impersonatedUserUuid: z.string().uuid(),
        startedAt: z.string().datetime(),
        endedAt: z.string().datetime().optional(),
        ipAddress: z.string().optional(),
        userAgent: z.string().optional(),
      }),
    ),
  ),
  'POST /api/v1/sa/impersonation/start/:userId': z.object({
    isImpersonating: z.boolean(),
    originalUser: userSchema,
    impersonatedUser: userSchema,
    startedAt: z.string().datetime(),
  }),
  'DELETE /api/v1/sa/impersonation/stop': z.object({
    message: z.string(),
  }),

  // ============================================
  // Super Admin Feature Flags
  // ============================================
  'GET /api/v1/sa/features': dataResponseSchema(
    z.array(
      z.object({
        key: z.string(),
        name: z.string(),
        description: z.string().optional(),
        enabled: z.boolean(),
        scope: z.enum(['system', 'company', 'user']),
      }),
    ),
  ),
  'GET /api/v1/sa/c/:company/features': dataResponseSchema(
    z.array(
      z.object({
        key: z.string(),
        name: z.string(),
        description: z.string().optional(),
        enabled: z.boolean(),
        scope: z.enum(['system', 'company', 'user']),
      }),
    ),
  ),
  'PUT /api/v1/sa/c/:company/features/:feature': z.object({
    key: z.string(),
    name: z.string(),
    description: z.string().optional(),
    enabled: z.boolean(),
    scope: z.enum(['system', 'company', 'user']),
  }),
  'POST /api/v1/sa/c/:company/features/bulk': z.object({
    message: z.string(),
    updated: z.array(z.string()),
  }),

  // ============================================
  // Super Admin Subscription Management
  // ============================================
  'GET /api/v1/sa/subscription-plans': dataResponseSchema(
    z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().optional(),
        price: moneySchema,
        features: z.array(z.string()),
        limits: z.record(z.number()).optional(),
        isActive: z.boolean(),
      }),
    ),
  ),
  'POST /api/v1/sa/subscription-plans': z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    price: moneySchema,
    features: z.array(z.string()),
    limits: z.record(z.number()).optional(),
    isActive: z.boolean(),
  }),
  'GET /api/v1/sa/c/:company/subscription': z.object({
    id: z.string(),
    companyUuid: z.string().uuid(),
    planId: z.string(),
    status: z.enum(['active', 'cancelled', 'expired', 'trial']),
    startDate: z.string().datetime(),
    endDate: z.string().datetime().optional(),
    trialEndDate: z.string().datetime().optional(),
  }),
  'POST /api/v1/sa/c/:company/subscribe': z.object({
    id: z.string(),
    companyUuid: z.string().uuid(),
    planId: z.string(),
    status: z.enum(['active', 'cancelled', 'expired', 'trial']),
    startDate: z.string().datetime(),
    endDate: z.string().datetime().optional(),
    trialEndDate: z.string().datetime().optional(),
  }),
  'PUT /api/v1/sa/c/:company/subscription': z.object({
    id: z.string(),
    companyUuid: z.string().uuid(),
    planId: z.string(),
    status: z.enum(['active', 'cancelled', 'expired', 'trial']),
    startDate: z.string().datetime(),
    endDate: z.string().datetime().optional(),
    trialEndDate: z.string().datetime().optional(),
  }),
  'DELETE /api/v1/sa/c/:company/subscription': z.object({
    message: z.string(),
  }),
  'GET /api/v1/sa/c/:company/usage': z.object({
    companyUuid: z.string().uuid(),
    period: z.string(),
    deliveries: z.number(),
    users: z.number(),
    vehicles: z.number(),
    apiCalls: z.number(),
    storage: z.number(),
  }),
  'GET /api/v1/sa/subscription/usage': z.object({
    totalCompanies: z.number(),
    activeSubscriptions: z.number(),
    totalRevenue: moneySchema,
    usageByPlan: z.record(z.number()),
  }),
  'POST /api/v1/sa/user': dataResponseSchema(userSchema),

  // ============================================
  // Billing Management
  // ============================================
  'GET /api/v1/c/:company/admin/billing': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    billingEmail: z.string().email().optional(),
    billingAddress: addressSchema.optional(),
    paymentTerms: z.number().optional(),
    currency: z.string().length(3),
    taxRate: z.number().optional(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'PUT /api/v1/c/:company/admin/billing': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    billingEmail: z.string().email().optional(),
    billingAddress: addressSchema.optional(),
    paymentTerms: z.number().optional(),
    currency: z.string().length(3),
    taxRate: z.number().optional(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'GET /api/v1/c/:company/admin/billing/stats': z.object({
    totalInvoices: z.number(),
    totalRevenue: moneySchema,
    pendingAmount: moneySchema,
    overdueAmount: moneySchema,
    averagePaymentTime: z.number(),
    period: z.string(),
  }),
  'GET /api/v1/c/:company/admin/billing/history': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        type: z.enum(['invoice', 'payment', 'refund', 'adjustment']),
        amount: moneySchema,
        description: z.string(),
        date: z.string().datetime(),
        status: z.string(),
      }),
    ),
  ),
  'POST /api/v1/c/:company/admin/billing/export': z.object({
    url: z.string().url(),
    expiresAt: z.string().datetime(),
    format: z.enum(['csv', 'pdf']),
  }),

  // ============================================
  // Invoice Management
  // ============================================
  'GET /api/v1/c/:company/admin/invoice': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        companyUuid: z.string().uuid(),
        invoiceNumber: z.string(),
        status: z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled']),
        amount: moneySchema,
        taxAmount: moneySchema.optional(),
        totalAmount: moneySchema,
        issueDate: z.string().datetime(),
        dueDate: z.string().datetime(),
        paidAt: z.string().datetime().optional(),
        clientName: z.string(),
        clientEmail: z.string().email().optional(),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
      }),
    ),
  ),
  'POST /api/v1/c/:company/admin/invoice': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    invoiceNumber: z.string(),
    status: z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled']),
    amount: moneySchema,
    taxAmount: moneySchema.optional(),
    totalAmount: moneySchema,
    issueDate: z.string().datetime(),
    dueDate: z.string().datetime(),
    paidAt: z.string().datetime().optional(),
    clientName: z.string(),
    clientEmail: z.string().email().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'GET /api/v1/c/:company/admin/invoice/:invoice': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    invoiceNumber: z.string(),
    status: z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled']),
    amount: moneySchema,
    taxAmount: moneySchema.optional(),
    totalAmount: moneySchema,
    issueDate: z.string().datetime(),
    dueDate: z.string().datetime(),
    paidAt: z.string().datetime().optional(),
    clientName: z.string(),
    clientEmail: z.string().email().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'PUT /api/v1/c/:company/admin/invoice/:invoice': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    invoiceNumber: z.string(),
    status: z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled']),
    amount: moneySchema,
    taxAmount: moneySchema.optional(),
    totalAmount: moneySchema,
    issueDate: z.string().datetime(),
    dueDate: z.string().datetime(),
    paidAt: z.string().datetime().optional(),
    clientName: z.string(),
    clientEmail: z.string().email().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'DELETE /api/v1/c/:company/admin/invoice/:invoice': z.object({
    message: z.string(),
  }),
  'POST /api/v1/c/:company/admin/invoice/:invoice/send': z.object({
    message: z.string(),
    sentAt: z.string().datetime(),
  }),
  'POST /api/v1/c/:company/admin/invoice/:invoice/mark-paid': z.object({
    message: z.string(),
    paidAt: z.string().datetime(),
  }),
  'GET /api/v1/c/:company/admin/invoice/:invoice/download': z.any(), // File download

  // ============================================
  // Transaction Management
  // ============================================
  'GET /api/v1/c/:company/admin/transaction': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        companyUuid: z.string().uuid(),
        type: z.enum(['payment', 'refund', 'adjustment', 'fee']),
        amount: moneySchema,
        status: z.enum(['pending', 'completed', 'failed', 'cancelled']),
        description: z.string(),
        reference: z.string().optional(),
        paymentMethod: z.string().optional(),
        invoiceUuid: z.string().uuid().optional(),
        processedAt: z.string().datetime().optional(),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
      }),
    ),
  ),
  'POST /api/v1/c/:company/admin/transaction': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    type: z.enum(['payment', 'refund', 'adjustment', 'fee']),
    amount: moneySchema,
    status: z.enum(['pending', 'completed', 'failed', 'cancelled']),
    description: z.string(),
    reference: z.string().optional(),
    paymentMethod: z.string().optional(),
    invoiceUuid: z.string().uuid().optional(),
    processedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'GET /api/v1/c/:company/admin/transaction/:transaction': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    type: z.enum(['payment', 'refund', 'adjustment', 'fee']),
    amount: moneySchema,
    status: z.enum(['pending', 'completed', 'failed', 'cancelled']),
    description: z.string(),
    reference: z.string().optional(),
    paymentMethod: z.string().optional(),
    invoiceUuid: z.string().uuid().optional(),
    processedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'PUT /api/v1/c/:company/admin/transaction/:transaction': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    type: z.enum(['payment', 'refund', 'adjustment', 'fee']),
    amount: moneySchema,
    status: z.enum(['pending', 'completed', 'failed', 'cancelled']),
    description: z.string(),
    reference: z.string().optional(),
    paymentMethod: z.string().optional(),
    invoiceUuid: z.string().uuid().optional(),
    processedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'DELETE /api/v1/c/:company/admin/transaction/:transaction': z.object({
    message: z.string(),
  }),
  'POST /api/v1/c/:company/admin/transaction/:transaction/refund': z.object({
    uuid: z.string().uuid(),
    amount: moneySchema,
    reason: z.string().optional(),
    processedAt: z.string().datetime(),
    message: z.string(),
  }),
  'GET /api/v1/c/:company/admin/transaction/stats': z.object({
    totalTransactions: z.number(),
    totalVolume: moneySchema,
    successRate: z.number(),
    averageAmount: moneySchema,
    period: z.string(),
  }),

  // ============================================
  // Payment Method Management
  // ============================================
  'GET /api/v1/c/:company/admin/payment/methods': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        companyUuid: z.string().uuid(),
        type: z.enum(['card', 'bank_account', 'digital_wallet']),
        name: z.string(),
        isDefault: z.boolean(),
        isActive: z.boolean(),
        lastFour: z.string().optional(),
        expiryDate: z.string().optional(),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
      }),
    ),
  ),
  'POST /api/v1/c/:company/admin/payment/methods': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    type: z.enum(['card', 'bank_account', 'digital_wallet']),
    name: z.string(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    lastFour: z.string().optional(),
    expiryDate: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'GET /api/v1/c/:company/admin/payment/methods/:method': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    type: z.enum(['card', 'bank_account', 'digital_wallet']),
    name: z.string(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    lastFour: z.string().optional(),
    expiryDate: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'PUT /api/v1/c/:company/admin/payment/methods/:method': z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    type: z.enum(['card', 'bank_account', 'digital_wallet']),
    name: z.string(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    lastFour: z.string().optional(),
    expiryDate: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  'DELETE /api/v1/c/:company/admin/payment/methods/:method': z.object({
    message: z.string(),
  }),
  'POST /api/v1/c/:company/admin/payment/methods/:method/set-default': z.object({
    message: z.string(),
  }),
  'POST /api/v1/c/:company/admin/payment/process': z.object({
    transactionId: z.string(),
    status: z.enum(['pending', 'completed', 'failed']),
    message: z.string(),
  }),

  // ============================================
  // User Profile Management
  // ============================================
  'GET /api/v1/c/:companySlug/admin/user/:userUuid/profile': dataResponseSchema(userSchema),
  'PUT /api/v1/c/:companySlug/admin/user/:userUuid/profile': dataResponseSchema(userSchema),
  'POST /api/v1/c/:companySlug/admin/user/:userUuid/change-password': z.object({
    message: z.string(),
  }),
  'GET /api/v1/c/:companySlug/admin/user/:userUuid/address': dataResponseSchema(z.array(addressSchema)),
  'POST /api/v1/c/:companySlug/admin/user/:userUuid/address': dataResponseSchema(addressSchema),
  'PUT /api/v1/c/:companySlug/admin/user/:userUuid/address/:addressUuid': dataResponseSchema(addressSchema),
  'DELETE /api/v1/c/:companySlug/admin/user/:userUuid/address/:addressUuid': z.object({
    message: z.string(),
  }),
  'GET /api/v1/c/:companySlug/admin/user/:userUuid/document': dataResponseSchema(z.array(documentSchema)),
  'POST /api/v1/c/:companySlug/admin/user/:userUuid/document': dataResponseSchema(documentSchema),
  'GET /api/v1/c/:companySlug/admin/user/:userUuid/document/:documentTypeSlug': dataResponseSchema(documentSchema),
  'DELETE /api/v1/c/:companySlug/admin/user/:userUuid/document/:documentTypeSlug': z.object({
    message: z.string(),
  }),
  'POST /api/v1/c/:companySlug/admin/user/:userUuid/profile-picture': z.object({
    profilePictureUrl: z.string().url(),
    message: z.string(),
  }),
  'GET /api/v1/c/:companySlug/admin/user/:userUuid/profile-picture/:format': z.any(), // File download
  'DELETE /api/v1/c/:companySlug/admin/user/:userUuid/profile-picture': z.object({
    message: z.string(),
  }),
  'GET /api/v1/c/:companySlug/admin/user/:userUuid/company': dataResponseSchema(z.array(companySchema)),
  'GET /api/v1/c/:companySlug/admin/user/:userUuid/role': dataResponseSchema(z.array(roleSchema)),
  'POST /api/v1/c/:companySlug/admin/user/:userUuid/role': dataResponseSchema(roleSchema),
  'DELETE /api/v1/c/:companySlug/admin/user/:userUuid/role/:role': z.object({
    message: z.string(),
  }),

  // ============================================
  // User CRUD Operations
  // ============================================
  'POST /api/v1/c/:companySlug/admin/user': dataResponseSchema(userSchema),
  'PUT /api/v1/c/:companySlug/admin/user/:userUuid': dataResponseSchema(userSchema),
  'DELETE /api/v1/c/:companySlug/admin/user/:userUuid': z.object({
    message: z.string(),
  }),

  // ============================================
  // Delivery Management CRUD Operations
  // ============================================
  'GET /api/v1/c/:companySlug/admin/delivery/:deliveryUuid': dataResponseSchema(deliverySchema),
  'PUT /api/v1/c/:companySlug/admin/delivery/:deliveryUuid': dataResponseSchema(deliverySchema),
  'DELETE /api/v1/c/:companySlug/admin/delivery/:deliveryUuid': z.object({
    message: z.string(),
  }),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/duplicate': dataResponseSchema(deliverySchema),

  // ============================================
  // Delivery Assignment Management
  // ============================================
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/assign-driver': dataResponseSchema(deliverySchema),
  'DELETE /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/unassign-driver': z.object({
    message: z.string(),
  }),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/assign-vehicle': dataResponseSchema(deliverySchema),
  'DELETE /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/unassign-vehicle': z.object({
    message: z.string(),
  }),

  // ============================================
  // Delivery Lifecycle Management
  // ============================================
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/start': dataResponseSchema(deliverySchema),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/complete': dataResponseSchema(deliverySchema),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/cancel': dataResponseSchema(deliverySchema),
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/reopen': dataResponseSchema(deliverySchema),

  // ============================================
  // Delivery Route Management
  // ============================================
  'POST /api/v1/c/:companySlug/admin/delivery/optimize-route': z.object({
    optimizedRoute: z.array(
      z.object({
        deliveryUuid: z.string().uuid(),
        order: z.number(),
        estimatedArrival: z.string().datetime(),
        estimatedDistance: z.number(),
      }),
    ),
    totalDistance: z.number(),
    totalDuration: z.number(),
  }),
  'GET /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/route': z.object({
    waypoints: z.array(
      z.object({
        lat: z.number(),
        lng: z.number(),
        address: z.string().optional(),
        order: z.number(),
      }),
    ),
    totalDistance: z.number(),
    estimatedDuration: z.number(),
  }),
  'PUT /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/route': z.object({
    waypoints: z.array(
      z.object({
        lat: z.number(),
        lng: z.number(),
        address: z.string().optional(),
        order: z.number(),
      }),
    ),
    totalDistance: z.number(),
    estimatedDuration: z.number(),
  }),

  // ============================================
  // Delivery Proof Management
  // ============================================
  'GET /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/proof': dataResponseSchema(
    z.array(
      z.object({
        uuid: z.string().uuid(),
        deliveryUuid: z.string().uuid(),
        proofType: z.string(),
        fileUrl: z.string().url(),
        createdAt: z.string().datetime(),
        createdByUserUuid: z.string().uuid(),
      }),
    ),
  ),
  'DELETE /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/proof/:proofId': z.object({
    message: z.string(),
  }),

  // ============================================
  // Delivery Notes Management
  // ============================================
  'POST /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/note': dataResponseSchema(noteSchema),
  'GET /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/note': dataResponseSchema(z.array(noteSchema)),
  'PUT /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/note/:noteId': dataResponseSchema(noteSchema),
  'DELETE /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/note/:noteId': z.object({
    message: z.string(),
  }),

  // ============================================
  // Delivery Analytics and Reporting
  // ============================================
  'GET /api/v1/c/:companySlug/admin/delivery/analytics': z.object({
    totalDeliveries: z.number(),
    completedDeliveries: z.number(),
    activeDeliveries: z.number(),
    cancelledDeliveries: z.number(),
    averageDeliveryTime: z.number(),
    onTimeDeliveryRate: z.number(),
    revenueByPeriod: z.array(
      z.object({
        period: z.string(),
        revenue: moneySchema,
      }),
    ),
    deliveriesByStatus: z.record(z.number()),
    topRoutes: z.array(
      z.object({
        from: z.string(),
        to: z.string(),
        count: z.number(),
      }),
    ),
  }),
  'POST /api/v1/c/:companySlug/admin/delivery/export': z.object({
    downloadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    format: z.enum(['csv', 'pdf']),
  }),

  // ============================================
  // Delivery Time Management
  // ============================================
  'POST /api/v1/c/:companySlug/admin/delivery/time-estimate': z.object({
    estimatedDuration: z.number(),
    estimatedDistance: z.number(),
    estimatedArrival: z.string().datetime(),
    confidence: z.number().min(0).max(1),
  }),
  'PUT /api/v1/c/:companySlug/admin/delivery/:deliveryUuid/eta': z.object({
    estimatedArrival: z.string().datetime(),
    updatedAt: z.string().datetime(),
    message: z.string(),
  }),

  // ============================================
  // Configuration Management
  // ============================================
  // System Configuration Endpoints
  'GET /api/v1/admin/configurations': dataResponseSchema(z.array(systemConfigurationSchema)),
  'GET /api/v1/admin/configurations/category/:category': dataResponseSchema(z.array(systemConfigurationSchema)),
  'GET /api/v1/admin/configurations/:key': dataResponseSchema(systemConfigurationSchema),
  'POST /api/v1/admin/configurations': dataResponseSchema(systemConfigurationSchema),
  'PUT /api/v1/admin/configurations/:key': dataResponseSchema(systemConfigurationSchema),
  'POST /api/v1/admin/configurations/cache/clear': z.object({
    message: z.string(),
    clearedAt: z.string().datetime(),
  }),

  // Company Configuration Endpoints
  'GET /api/v1/c/:companySlug/admin/configurations': dataResponseSchema(z.array(companyConfigurationSchema)),
  'GET /api/v1/c/:companySlug/admin/configurations/category/:category': dataResponseSchema(
    z.array(companyConfigurationSchema),
  ),
  'GET /api/v1/c/:companySlug/admin/configurations/:key': dataResponseSchema(companyConfigurationSchema),
  'GET /api/v1/c/:companySlug/admin/configurations/:key/history': dataResponseSchema(
    z.array(companyConfigurationHistorySchema),
  ),
  'PUT /api/v1/c/:companySlug/admin/configurations/:key': dataResponseSchema(companyConfigurationSchema),
  'DELETE /api/v1/c/:companySlug/admin/configurations/:key': z.object({
    message: z.string(),
  }),
};

// Export individual schemas for reuse
export {
  addressSchema,
  calendarEventSchema,
  companySchema,
  deliveryContactSchema,
  deliverySchema,
  deliveryStepTaskDocInfoSchema,
  deliveryStepTaskDocSchema,
  documentSchema,
  driverInfoSchema,
  noteSchema,
  parcelSchema,
  typeBaseSchema,
  typeMapSchema,
  userSchema,
  vehicleSchema,
};
